import { renderPageShell } from '../lib/page-shell';
import {
  fetchAnalytics,
  fetchIntegrity,
  getAdminToken,
  reconcileStore,
  refundPurchase,
  setAdminToken,
  type AnalyticsReport,
  type IntegrityAssessment,
  type ReconciliationReport,
} from '../lib/admin';
import type { RouteHandler } from '../lib/router';

/** Stat-tile style compact formatting (1,284 / 12.9K / 4.2M) — proportional figures, never tabular, per the dataviz skill's figures guidance. */
function compact(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function table(caption: string, columns: { key: string; label: string; num?: boolean }[], rows: Record<string, string | number>[]): string {
  if (rows.length === 0) return `<div class="admin-table-wrap"><table class="admin-table"><caption>${caption}</caption></table><p class="settings-hint">No rows yet.</p></div>`;
  return `
    <div class="admin-table-wrap">
      <table class="admin-table">
        <caption>${caption}</caption>
        <thead><tr>${columns.map((c) => `<th class="${c.num ? 'num' : ''}">${c.label}</th>`).join('')}</tr></thead>
        <tbody>
          ${rows
            .map(
              (row) =>
                `<tr>${columns.map((c) => `<td class="${c.num ? 'num' : ''}">${c.num ? compact(Number(row[c.key])) : row[c.key]}</td>`).join('')}</tr>`,
            )
            .join('')}
        </tbody>
      </table>
    </div>
  `;
}

// C010 (launch operations): a thin UI over the moderation-only,
// `ADMIN_TOKEN`-gated read/write surfaces built in C007/C009
// (admin-routes.ts, store-admin-routes.ts) — until now both were raw JSON
// with no frontend at all. Deliberately unlinked from the primary nav; a
// moderator navigates here directly (`#/admin`), same "doesn't advertise
// itself" posture as the endpoints it calls.

export const renderAdmin: RouteHandler = (container, navigate) => {
  const body = renderPageShell(container, navigate, 'admin', {
    title: 'ADMIN',
    subtitle: 'Moderation only. Requires the deploy’s ADMIN_TOKEN; everything here 404s or 403s without it.',
    body: `
      <div class="admin-panel">
        <section class="choice-card">
          <strong>Admin token</strong>
          <small>Stored only in this browser (localStorage), sent as X-Admin-Token. Never your player session token.</small>
          <input class="join-input" id="admin-token-input" type="password" placeholder="Admin token" aria-label="Admin token" autocomplete="off" />
          <button class="button button--secondary" type="button" data-action="save-token">Save token</button>
          <p class="form-error" data-role="token-status" aria-live="polite"></p>
        </section>

        <section class="choice-card">
          <strong>Account integrity</strong>
          <small>C007 Layer 3 statistical assessment: heuristic, best-effort, never an automatic ban.</small>
          <input class="join-input" id="integrity-username" placeholder="Username" aria-label="Username to check" autocomplete="off" />
          <button class="button button--secondary" type="button" data-action="check-integrity">Check</button>
          <div id="integrity-result" aria-live="polite"></div>
        </section>

        <section class="choice-card">
          <strong>Store reconciliation</strong>
          <small>Ledger-vs-entitlement consistency + stuck pending sessions. Internal check, not a live Stripe cross-check.</small>
          <button class="button button--secondary" type="button" data-action="run-reconcile">Run reconciliation</button>
          <div id="reconcile-result" aria-live="polite"></div>
        </section>

        <section class="choice-card">
          <strong>Refund a purchase</strong>
          <small>Revokes the cosmetic entitlement or debits the currency-pack amount back out. Idempotent.</small>
          <input class="join-input" id="refund-session-id" placeholder="Checkout session id" aria-label="Checkout session id to refund" autocomplete="off" />
          <button class="button button--secondary" type="button" data-action="run-refund">Refund</button>
          <div id="refund-result" aria-live="polite"></div>
        </section>

        <section class="choice-card admin-panel--wide">
          <strong>Analytics</strong>
          <small>
            Aggregated from the ledgers this project already writes for correctness (XP, currency, item grants,
            match history, achievements), not a separate event-tracking system. Wallet figures are TEST MODE
            unless this deploy ever set real Stripe secrets.
          </small>
          <div class="hero-actions">
            <input class="join-input" id="analytics-days" type="number" min="1" max="90" value="14" style="max-width: 8rem;" aria-label="Time-series window, in days" />
            <button class="button button--secondary" type="button" data-action="run-analytics">Load analytics</button>
          </div>
          <div id="analytics-result" aria-live="polite"></div>
        </section>
      </div>
    `,
  });

  const tokenInput = body.querySelector<HTMLInputElement>('#admin-token-input')!;
  const tokenStatus = body.querySelector<HTMLParagraphElement>('[data-role="token-status"]')!;
  tokenInput.value = getAdminToken();
  tokenStatus.textContent = getAdminToken() ? 'A token is saved in this browser.' : 'No token saved yet.';

  body.querySelector<HTMLButtonElement>('[data-action="save-token"]')?.addEventListener('click', () => {
    setAdminToken(tokenInput.value.trim());
    tokenStatus.textContent = tokenInput.value.trim() ? 'Saved.' : 'Cleared.';
  });

  function renderError(mount: Element, error: unknown) {
    mount.innerHTML = `<p class="form-error">${error instanceof Error ? error.message : 'Request failed.'}</p>`;
  }

  function renderIntegrity(mount: Element, userId: string, username: string, assessment: IntegrityAssessment) {
    const verdictClass = assessment.verdict === 'clean' ? '' : assessment.verdict === 'insufficient-data' ? '' : 'form-error';
    mount.innerHTML = `
      <p><strong>${username}</strong> (${userId}): <span class="${verdictClass}">${assessment.verdict.toUpperCase()}</span>, ${assessment.sampleSize} rounds sampled</p>
      <p class="settings-hint">${assessment.note}</p>
      <ul class="settings-list">
        ${assessment.signals
          .map(
            (s) => `<li><label><span>${s.fired ? '⚠' : '·'} ${s.name}: ${s.metric.toFixed(2)} (threshold ${s.threshold}): ${s.description}</span></label></li>`,
          )
          .join('')}
      </ul>
    `;
  }

  function renderReconciliation(mount: Element, report: ReconciliationReport) {
    mount.innerHTML = `
      <p>${report.clean ? 'Clean.' : 'Drift found.'} Checked ${report.completedChecked} completed / ${report.pendingChecked} pending sessions, as of ${new Date(report.generatedAt).toLocaleString()}.</p>
      ${
        report.drift.length > 0
          ? `<ul class="settings-list">${report.drift.map((d) => `<li><label><span class="form-error">${d.issue}: session ${d.sessionId}, user ${d.userId} (${d.kind})</span></label></li>`).join('')}</ul>`
          : ''
      }
      ${
        report.stuckPending.length > 0
          ? `<ul class="settings-list">${report.stuckPending.map((s) => `<li><label><span class="form-error">stuck pending: session ${s.sessionId}, user ${s.userId}, ${Math.round(s.ageMs / 60000)}m old</span></label></li>`).join('')}</ul>`
          : ''
      }
    `;
  }

  function renderAnalytics(mount: Element, report: AnalyticsReport) {
    const currencyLabel: Record<string, string> = { ticks: 'Ticks', tocks: 'Tocks' };

    mount.innerHTML = `
      <p class="settings-hint">As of ${new Date(report.generatedAt).toLocaleString()}. Time series covers the last ${report.windowDays} day(s).</p>

      <h3 class="history-heading">Growth &amp; activity</h3>
      <ul class="match-facts" aria-label="Growth and activity headline numbers">
        <li><strong>${compact(report.growth.totalUsers)}</strong><span>Total accounts</span></li>
        <li><strong>${compact(report.activity.active7d)}</strong><span>Active, 7d</span></li>
        <li><strong>${compact(report.activity.active30d)}</strong><span>Active, 30d</span></li>
        <li><strong>${compact(report.activity.totalMatches)}</strong><span>Matches played</span></li>
        <li><strong>${compact(report.activity.totalRankedMatches)}</strong><span>Ranked matches</span></li>
      </ul>
      ${table(
        'Registrations by day',
        [
          { key: 'day', label: 'Day' },
          { key: 'count', label: 'Registrations', num: true },
        ],
        report.growth.registrationsByDay,
      )}
      ${table(
        'Activity by day',
        [
          { key: 'day', label: 'Day' },
          { key: 'matches', label: 'Matches', num: true },
          { key: 'rankedMatches', label: 'Ranked', num: true },
          { key: 'activePlayers', label: 'Active players', num: true },
        ],
        report.activity.byDay,
      )}

      <h3 class="history-heading">Progression</h3>
      <ul class="match-facts" aria-label="Progression headline numbers">
        <li><strong>${compact(report.progression.achievements.totalUnlocks)}</strong><span>Achievement unlocks</span></li>
        <li><strong>${compact(report.progression.achievements.playersWithAny)}</strong><span>Players with 1+</span></li>
        <li><strong>${compact(report.progression.xp.totalGranted)}</strong><span>Total XP granted</span></li>
      </ul>
      ${table(
        'Level distribution',
        [
          { key: 'level', label: 'Level', num: true },
          { key: 'count', label: 'Players', num: true },
        ],
        report.progression.levelDistribution,
      )}
      ${table(
        'Most-unlocked achievements',
        [
          { key: 'name', label: 'Achievement' },
          { key: 'count', label: 'Unlocks', num: true },
        ],
        report.progression.achievements.topUnlocked,
      )}
      ${table(
        'Rarest unlocked achievements (excludes never-unlocked)',
        [
          { key: 'name', label: 'Achievement' },
          { key: 'count', label: 'Unlocks', num: true },
        ],
        report.progression.achievements.rarestUnlocked,
      )}
      ${table(
        'XP grants by reason',
        [
          { key: 'reason', label: 'Reason' },
          { key: 'total', label: 'Total XP', num: true },
          { key: 'grants', label: 'Grants', num: true },
        ],
        report.progression.xp.byReason,
      )}

      <h3 class="history-heading">Economy</h3>
      ${table(
        'Currency balances outstanding',
        [
          { key: 'currency', label: 'Currency' },
          { key: 'balance', label: 'Balance', num: true },
        ],
        report.economy.currency.outstandingBalances.map((b) => ({ ...b, currency: currencyLabel[b.currency] ?? b.currency })),
      )}
      ${table(
        'Currency flow by reason (net, negative = spend)',
        [
          { key: 'currency', label: 'Currency' },
          { key: 'reason', label: 'Reason' },
          { key: 'net', label: 'Net', num: true },
          { key: 'entries', label: 'Entries', num: true },
        ],
        report.economy.currency.byReason.map((r) => ({ ...r, currency: currencyLabel[r.currency] ?? r.currency })),
      )}
      ${table(
        'Item grants by reason',
        [
          { key: 'reason', label: 'Reason' },
          { key: 'count', label: 'Items granted', num: true },
        ],
        report.economy.items.byReason,
      )}
      ${table(
        'Wallet ledger (TEST MODE unless real Stripe secrets were set)',
        [
          { key: 'kind', label: 'Kind' },
          { key: 'status', label: 'Status' },
          { key: 'count', label: 'Count', num: true },
          { key: 'cents', label: 'Cents', num: true },
        ],
        report.economy.wallet,
      )}

      <h3 class="history-heading">Social</h3>
      <ul class="match-facts" aria-label="Social headline numbers">
        <li><strong>${compact(report.social.totalBlocks)}</strong><span>Blocks</span></li>
      </ul>
      ${table(
        'Friendships by status',
        [
          { key: 'status', label: 'Status' },
          { key: 'count', label: 'Count', num: true },
        ],
        report.social.friendships,
      )}
      ${table(
        'Direct challenges by status',
        [
          { key: 'status', label: 'Status' },
          { key: 'count', label: 'Count', num: true },
        ],
        report.social.challenges,
      )}
    `;
  }

  body.querySelector<HTMLButtonElement>('[data-action="check-integrity"]')?.addEventListener('click', async () => {
    const mount = body.querySelector('#integrity-result')!;
    const username = body.querySelector<HTMLInputElement>('#integrity-username')!.value.trim();
    if (!username) return;
    mount.innerHTML = '<p class="page-body__stub">Checking…</p>';
    try {
      const { userId, username: resolvedUsername, assessment } = await fetchIntegrity(username);
      renderIntegrity(mount, userId, resolvedUsername, assessment);
    } catch (error) {
      renderError(mount, error);
    }
  });

  body.querySelector<HTMLButtonElement>('[data-action="run-reconcile"]')?.addEventListener('click', async () => {
    const mount = body.querySelector('#reconcile-result')!;
    mount.innerHTML = '<p class="page-body__stub">Running…</p>';
    try {
      renderReconciliation(mount, await reconcileStore());
    } catch (error) {
      renderError(mount, error);
    }
  });

  body.querySelector<HTMLButtonElement>('[data-action="run-refund"]')?.addEventListener('click', async () => {
    const mount = body.querySelector('#refund-result')!;
    const sessionId = body.querySelector<HTMLInputElement>('#refund-session-id')!.value.trim();
    if (!sessionId) return;
    mount.innerHTML = '<p class="page-body__stub">Refunding…</p>';
    try {
      const result = await refundPurchase(sessionId);
      mount.innerHTML = `<p>${result.refunded ? `Refunded (${result.kind}).` : 'Already refunded, or not a completed purchase, no change made.'}</p>`;
    } catch (error) {
      renderError(mount, error);
    }
  });

  body.querySelector<HTMLButtonElement>('[data-action="run-analytics"]')?.addEventListener('click', async () => {
    const mount = body.querySelector('#analytics-result')!;
    const days = Number(body.querySelector<HTMLInputElement>('#analytics-days')!.value) || 14;
    mount.innerHTML = '<p class="page-body__stub">Loading…</p>';
    try {
      renderAnalytics(mount, await fetchAnalytics(days));
    } catch (error) {
      renderError(mount, error);
    }
  });
};
